import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type { HouseholdGroupResponse } from "../../../../api/src/household-groups/household-groups.service";
import Loading from "../../Loading";
import {
  useQueryBillingMinimums,
  useQueryFeeStructures,
} from "../../billing/lib";
import ActionButton from "../../components/ActionButton";
import TabContainerWithTabs from "../../components/TabContainer";
import { Table, useTable } from "../../components/Table/Table";
import ManageNav from "../ManageNav";
import CreateHouseholdGroupDialog from "./CreateHouseholdGroupDialog";
import { useQueryHouseholdGroups } from "./lib";

type HouseholdGroupRow = HouseholdGroupResponse & {
  householdCount: number;
  feeStructureName: string;
  billingMinimumName: string;
  /** The fee structure read failed: the cell shows the em dash. */
  feeStructureFailed: boolean;
  /** The billing minimum read failed: the cell shows the em dash. */
  billingMinimumFailed: boolean;
};

const EM_DASH = "\u2014";

/**
 * The number of membership rows of a group in force on `today`: the row starts
 * on or before `today`, and its last day, the earlier of its `endDate` and the
 * group's `dissolvedEffectiveDate`, is none or on or after `today`. All dates
 * are `YYYY-MM-DD`, so string order is date order.
 */
export const countHouseholdsOnDate = (
  group: Pick<HouseholdGroupResponse, "members" | "dissolvedEffectiveDate">,
  today: string,
) =>
  (group.members ?? []).filter((member) => {
    const lastDays = [member.endDate, group.dissolvedEffectiveDate].filter(
      (date): date is string => date != null,
    );
    const lastDay = lastDays.length > 0 ? lastDays.sort()[0] : null;
    return (
      member.effectiveDate <= today && (lastDay == null || lastDay >= today)
    );
  }).length;

const columnHelper = createColumnHelper<HouseholdGroupRow>();

const columns = [
  columnHelper.accessor((row) => row.name, {
    id: "name",
    cell: (info) => (
      <Link
        to={`/clients/households/household-groups/${info.row.original.householdGroupId}`}
      >
        {info.getValue()}
      </Link>
    ),
    header: () => "Name",
  }),
  columnHelper.accessor((row) => row.householdCount, {
    id: "households",
    header: () => "Households",
    enableColumnFilter: false,
  }),
  columnHelper.accessor((row) => row.feeStructureName, {
    id: "feeStructure",
    header: () => "Fee Structure",
    cell: (info) =>
      info.row.original.feeStructureFailed ? EM_DASH : info.getValue(),
  }),
  columnHelper.accessor((row) => row.billingMinimumName, {
    id: "billingMinimum",
    header: () => "Assigned Billing Minimum",
    cell: (info) =>
      info.row.original.billingMinimumFailed ? EM_DASH : info.getValue(),
  }),
  columnHelper.accessor((row) => row.dissolvedEffectiveDate ?? "", {
    id: "dissolved",
    header: () => "Archived",
    enableColumnFilter: false,
  }),
];

const HouseholdGroups = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const {
    isPending: isPendingGroups,
    isError: isErrorGroups,
    data: groups,
    refetch: refetchGroups,
  } = useQueryHouseholdGroups();

  const {
    isPending: isPendingFeeStructures,
    isError: isErrorFeeStructures,
    data: dataFeeStructures,
    refetch: refetchFeeStructures,
  } = useQueryFeeStructures();

  const {
    isPending: isPendingBillingMinimums,
    isError: isErrorBillingMinimums,
    data: dataBillingMinimums,
    refetch: refetchBillingMinimums,
  } = useQueryBillingMinimums();

  const rows = useMemo(() => {
    // Today's UTC date, the date the api reads the membership on, as
    // `HouseholdsInTwoGroupsAlert` (`client/src/billing/BillingSummary.tsx`)
    // does. The browser's local date can be a day before or after it.
    const today = new Date().toISOString().substring(0, 10);
    return (groups ?? []).map((group) => ({
      ...group,
      householdCount: countHouseholdsOnDate(group, today),
      feeStructureName:
        (dataFeeStructures?.data ?? []).find(
          (feeStructure) =>
            feeStructure.feeStructureId === group.feeStructureId,
        )?.name ?? "",
      billingMinimumName:
        group.billingMinimumId == null
          ? ""
          : ((dataBillingMinimums?.data ?? []).find(
              (billingMinimum) => billingMinimum.id === group.billingMinimumId,
            )?.name ?? ""),
      feeStructureFailed: isErrorFeeStructures,
      billingMinimumFailed: isErrorBillingMinimums,
    }));
  }, [
    groups,
    dataFeeStructures?.data,
    dataBillingMinimums?.data,
    isErrorFeeStructures,
    isErrorBillingMinimums,
  ]);

  const { table } = useTable({
    columns,
    data: rows,
    getRowId: (row) => row.householdGroupId.toString(),
    initialState: { sorting: [{ id: "name", desc: false }] },
  });

  return (
    <TabContainerWithTabs tabs={ManageNav}>
      <Row>
        <Col>
          <h1>Household Groups</h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="New"
                icon="/icons/new.svg"
                onClick={() => setShowCreateDialog(true)}
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          {isPendingGroups ||
          isPendingFeeStructures ||
          isPendingBillingMinimums ? (
            <Loading />
          ) : isErrorGroups ? (
            <Alert variant="danger">
              An error occurred{" "}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => refetchGroups()}
              >
                Try again
              </Button>
            </Alert>
          ) : (
            <>
              {(isErrorFeeStructures || isErrorBillingMinimums) && (
                <Alert variant="danger">
                  Failed to load the fee structure and billing minimum names{" "}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      if (isErrorFeeStructures) refetchFeeStructures();
                      if (isErrorBillingMinimums) refetchBillingMinimums();
                    }}
                  >
                    Try again
                  </Button>
                </Alert>
              )}
              {!groups || groups.length <= 0 ? (
                <Alert>No household groups found</Alert>
              ) : (
                <Table table={table} />
              )}
            </>
          )}
        </Col>
      </Row>
      <CreateHouseholdGroupDialog
        show={showCreateDialog}
        onHide={() => setShowCreateDialog(false)}
      />
    </TabContainerWithTabs>
  );
};

export default HouseholdGroups;
